import { Box, Grid, Link as MuiLink, styled, Typography } from '@mui/material'

import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import useSmallScreen from '@ecm/ui/hook/UseSmallScreen'

import greenGcBg from '/public/images/illustration/green_gc_bg.png'
import greenNffBg from '/public/images/illustration/green_nff_bg.png'
import ecmDayLogo from '/public/images/logo/ecm_earth_day_logo.svg'
import grassrootsCarbonLogo from '/public/images/logo/grassroots_carbon.svg'
import nationalForestFoundationLogo from '/public/images/logo/national-forest-foundation.svg'

const partners = [
  {
    description:
      'We’re partnering with the National Forest Foundation, the leading organization inspiring personal and meaningful connections to our National Forests, to support reforestation in areas that need it most.',
    icon: nationalForestFoundationLogo,
    image: greenNffBg,
    link: 'https://www.nationalforests.org',
    name: 'National Forest Foundation',
  },
  {
    description:
      'We’re partnering with Grassroots Carbon to purchase corporate carbon credits and reduce our carbon footprint with landowners who are engaged in regenerative practices.',
    icon: grassrootsCarbonLogo,
    image: greenGcBg,
    link: 'https://buildgrassroots.com',
    name: 'Grassroots Carbon',
  },
]

type PartnersProps = {
  className?: string
}

const Partners = styled(({ className }: PartnersProps) => {
  const isSmallScreen = useSmallScreen()

  return (
    <Box className={className}>
      <Box bgcolor={Color.smoke.lightest}>
        <MainContainer
          sx={{
            py: '3em',
          }}
        >
          <Box m="0 auto" maxWidth="900px" textAlign="center">
            <CloudflareImage src={ecmDayLogo} />
            <Typography
              variant="h4"
              component="p"
              className="gradientText"
              maxWidth="900px"
              m="1em auto"
            >
              To keep mail green, we recycle 100% of our customers’ junk mail and partner with
              nonprofit and sustainable organizations to protect and restore our planet.
            </Typography>
            <Typography variant="p1" maxWidth="700px" m="0 auto">
              Together with our partners, we are building and growing Earth Class Mail as a
              sustainable company for our customers.
            </Typography>
          </Box>
        </MainContainer>
      </Box>
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${
            Color.smoke.lightest
          } 238px, rgba(255, 255, 255) ${isSmallScreen ? '50%' : '238px'})`,
        }}
      >
        <MainContainer
          sx={{
            py: '3em',
          }}
        >
          <Box
            display="grid"
            gap="2em"
            gridTemplateColumns={{
              lg: '0.5fr 1fr 1fr 0.5fr',
              md: '0.2fr 1fr 1fr 0.2fr',
              sm: '1fr',
            }}
            justifyItems={{ md: 'unset', xs: 'center' }}
          >
            {partners.map((partner, index) => (
              <MuiLink
                key={partner.name}
                borderRadius="8px"
                boxShadow="0px 10px 20px rgba(23, 41, 115, 0.06)"
                bgcolor={Color.white.normal}
                maxWidth="400px"
                gridColumn={{ md: `${index + 2}/ span 1`, sm: `1 / span ${index}` }}
                href={partner.link}
                target="_blank"
                rel="noreferrer nofollow"
                underline="none"
                sx={{
                  ':hover': {
                    boxShadow: '0px 10px 20px rgba(23, 41, 115, 0.16)',
                  },
                  transition: 'box-shadow 0.15s ease-in-out',
                }}
              >
                <Grid
                  container
                  key={partner.name}
                  p="2em"
                  position="relative"
                  height="190px"
                  bgcolor={Color.saint.normal}
                  borderRadius="8px 8px 0 0"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CloudflareImage
                    className="background-img"
                    src={partner.image}
                    layout="fill"
                    objectFit="cover"
                  />
                  <CloudflareImage src={partner.icon} />
                </Grid>
                <Box p="1.5em">
                  <Typography variant="h5" component="h3" color={Color.saint.normal}>
                    {partner.name}
                  </Typography>
                  <Typography variant="p3" fontWeight={400} my="0.5em">
                    {partner.description}
                  </Typography>
                </Box>
              </MuiLink>
            ))}
          </Box>
        </MainContainer>
      </Box>
    </Box>
  )
})({
  '.background-img': {
    borderRadius: '8px 8px 0 0',
    opacity: 0.6,
  },
  '.gradientText': {
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundImage: 'linear-gradient(272.81deg, #4EDF97 11.53%, #0F2272 100%)',
  },
})

export default Partners
